import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import ReactPlayer from 'react-player';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import { Title } from '../../utils/title';
import contentful from '../../utils/contentful';
import Layout from './Layout';
import styles from './EntryDetail.module.scss';

import hs from '../../assets/brands/hs.png';
import pmp from '../../assets/brands/pmp.png';
import ptt from '../../assets/brands/ptt.png';
import usrx from '../../assets/brands/usrx.png';

const EntryMore = () => {

  const items = [
    {
      label: 'Head and Shoulders',
      image: hs,
      link: '/case-studies/head-shoulders-bare',
    },
    {
      label: 'Pompeian Olive Oil',
      image: pmp,
      link: '/case-studies/pompeian',
    },
    {
      label: 'Pharm to Table',
      image: ptt,
      link: '/case-studies/pharm-to-table',
    },
    {
      label: 'Urban Skin Rx',
      image: usrx,
      link: '/case-studies/urban-skin-rx',
    },
  ];

  return (
    <div className={styles.more}>
      {
        items.map((item, index) => (
          <Link
            key={index}
            to={item.link}
            aria-label={item.label}>

            <div className={styles.image}>
              <LazyLoadImage
                src={item.image}
                alt={item.label}
              />
            </div>

            <div className={styles.label}>
              {item.label}
            </div>

          </Link>
        ))
      }

    </div>
  );
};

const EntryDetail = ({ query }) => {

  const { slug } = useParams();

  const [ loading, setLoading ] = useState(false);
  const [ item, setItem ] = useState();

  useEffect(() => {

    setLoading(true);

    contentful.client
      .getEntries({
        ...query,
        'fields.slug': slug,
      })
      .then(res => {
        setLoading(false);

        if (res.items.length > 0) {
          setItem(res.items[0]);
        } else {
          setItem(null);
        }
      })
      .catch(err => {
        setLoading(false);
        setItem(null);
      });

  }, [query, slug]);

  const renderContent = (content) => {

    if (content && content.nodeType === 'document') {

      const options = {
        renderNode: {
          [BLOCKS.EMBEDDED_ENTRY]: (node) => {

            const contentTypeId = node.data.target.sys.contentType.sys.id;

            if (contentTypeId === 'componentRichImage') {

              const image = node.data.target.fields.image;

              if (image && image.fields) {
                return (
                  <LazyLoadImage
                    className={styles.media}
                    src={`https:${image.fields.file.url}`}
                    alt={
                      image.fields.description ||
                      image.fields.title
                    }
                  />
                );
              }
            }

            return null;
          }
        },
      };

      return documentToReactComponents(content, options);
    }

    return null;
  };

  const renderValue = (value) => {

    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    
    if (Array.isArray(value)) {
      return value.map((item, index) => (
        <div key={index}>
          {item}
        </div>
      ));
    }
    
    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return null;
  };

  const kind = item && item.sys.contentType.sys.id;

  return (
    <Layout>
      <div className={styles.root}>

        {
          loading && (
            <div className={styles.loading}>
              Loading...
            </div>
          )
        }

        {
          item &&
          item.fields && (
            <>
              <Title value={item.fields.title} />

              <div className={styles.title}>
                {item.fields.title}
              </div>

              <div className={styles.date}>
                {new Date(item.fields.publishedDate).toLocaleDateString()}
              </div>

              {
                /*
                 * ARTICLE
                 */

                kind === 'pageBlogPost' && (
                  <div className={`${styles.content} ${styles.article}`}>
                    <div className={styles.image}>
                      {
                        item.fields.featuredImage &&
                        item.fields.featuredImage.fields && (
                          <LazyLoadImage
                            src={`https:${item.fields.featuredImage.fields.file.url}`}
                            alt={
                              item.fields.featuredImage.fields.description ||
                              item.fields.featuredImage.fields.title ||
                              item.fields.title
                            }
                          />
                        )
                      }
                    </div>
                    {renderContent(item.fields.content)}
                  </div>
                )
              }

              {
                /*
                 * CASE STUDY
                 */

                kind === 'caseStudy' && (
                  <div className={`${styles.content} ${styles.case}`}>
                    <div className={styles.side}>
                      {
                        /*
                         * BRAND LOGO
                         */
                        item.fields.brandLogo &&
                        item.fields.brandLogo.fields &&
                        item.fields.brandLogo.fields.file && (
                          <LazyLoadImage
                            src={item.fields.brandLogo.fields.file.url}
                            alt={item.fields.brandLogo.fields.title}
                          />
                        )
                      }

                      {
                        /*
                         * FEATURED IMAGE
                         */
                        item.fields.featuredImage &&
                        item.fields.featuredImage.fields &&
                        item.fields.featuredImage.fields.file && (
                          <LazyLoadImage
                            src={item.fields.featuredImage.fields.file.url}
                            alt={item.fields.featuredImage.fields.title}
                          />
                        )
                      }
                    </div>

                    <div className={styles.main}>
                      {
                        /*
                         * BRAND NAME
                         */
                        item.fields.brand && (
                          <div className={`${styles.section} ${styles.brand}`}>
                            <div className={styles.label}>
                              Brand
                            </div>

                            <div className={styles.value}>
                              {renderValue(item.fields.brand)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * AGENCY NAME
                         */
                        item.fields.agency && (
                          <div className={`${styles.section} ${styles.agency}`}>
                            <div className={styles.label}>
                              Agency
                            </div>

                            <div className={styles.value}>
                              {renderValue(item.fields.agency)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * MAIN CONTENT
                         */
                        item.fields.mainContent && (
                          <div className={`${styles.section} ${styles.content}`}>
                            {renderContent(item.fields.mainContent)}
                          </div>
                        )
                      }

                      {
                        /*
                         * OBJECTIVE
                         */
                        item.fields.objective && (
                          <div className={`${styles.section} ${styles.objective}`}>
                            <div className={styles.label}>
                              Objective
                            </div>

                            <div className={styles.content}>
                              {renderContent(item.fields.objective)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * RESULTS
                         */
                        item.fields.results && (
                          <div className={`${styles.section} ${styles.results}`}>
                            <div className={styles.label}>
                              Results
                            </div>

                            <div className={styles.content}>
                              {renderContent(item.fields.results)}
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * STATS
                         */
                        item.fields.stats && (
                          <div className={`${styles.section} ${styles.stats}`}>
                            <table>
                              <tbody>
                                {
                                  item.fields.stats.data.map((stat, index) => (
                                    <tr key={index}>
                                      <td>{renderValue(stat.label)}</td>
                                      <td>{renderValue(stat.value)}</td>
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                          </div>
                        )
                      }

                      {
                        /*
                         * SAMPLE CONTENT
                         */
                        item.fields.sampleContent &&
                        item.fields.sampleContent.length > 0 && (
                          <div className={`${styles.section} ${styles.samples}`}>
                            <div className={styles.label}>
                              Sample Content
                            </div>

                            <div className={styles.items}>
                              {
                                item.fields.sampleContent.map((media, index) => {

                                  const file = (
                                    media &&
                                    media.fields &&
                                    media.fields.file
                                  );

                                  if (!file) {
                                    return null;
                                  }

                                  const title = media.fields.title;

                                  return (
                                    <div key={index}>
                                      {
                                        file.contentType?.startsWith('video') ? (
                                          <ReactPlayer
                                            src={file.url}
                                            alt={title}
                                            controls={true}
                                          />
                                        ) : (
                                          <LazyLoadImage
                                            src={file.url}
                                            alt={title}
                                          />
                                        )
                                      }
                                    </div>
                                  );
                                })
                              }
                            </div>
                          </div>
                        )
                      }

                      {
                        /*
                         * ACTION
                         */
                        (
                          <div className={`${styles.section} ${styles.action}`}>
                            <Link to='?dialog=contact'>
                              Get Reviews
                            </Link>
                          </div>
                        )
                      }
                    </div>
                  </div>
                )
              }
            </>
          )
        }

        {
          kind === 'caseStudy' && (
            <EntryMore />
          )
        }

      </div>
    </Layout>
  );
};

export default EntryDetail;